import { CAPTURE_WORKLET, workletURL } from '@/features/test-call/lib/worklets'

/**
 * Запись надиктованного.
 *
 * Пишет тем же трактом, что и звонок: PCM16, шестнадцать килогерц,
 * моно. Это не прихоть — распознавание на той стороне ждёт именно такой
 * WAV, и перекодировать webm от MediaRecorder пришлось бы на сервере,
 * притащив туда ffmpeg ради одной кнопки.
 */
export class Dictation {
  private audio: AudioContext | null = null
  private mic: MediaStream | null = null
  private capture: AudioWorkletNode | null = null
  private chunks: Float32Array[] = []

  private readonly rate = 16000

  constructor(private readonly onLevel?: (level: number) => void) {}

  /** Начинает запись. Бросает, если микрофон не дали. */
  async start(): Promise<void> {
    this.mic = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    })

    this.audio = new AudioContext({ sampleRate: this.rate })
    await this.audio.audioWorklet.addModule(workletURL(CAPTURE_WORKLET))

    const source = this.audio.createMediaStreamSource(this.mic)
    this.capture = new AudioWorkletNode(this.audio, 'capture')

    this.chunks = []
    this.capture.port.onmessage = (e) => {
      const block = e.data as Float32Array
      this.chunks.push(block)

      if (this.onLevel) {
        let sum = 0
        for (let i = 0; i < block.length; i++) sum += (block[i] ?? 0) ** 2
        this.onLevel(Math.sqrt(sum / block.length))
      }
    }

    source.connect(this.capture)
  }

  /** Останавливает запись и отдаёт готовый WAV. */
  stop(): Blob {
    this.mic?.getTracks().forEach((track) => track.stop())
    void this.audio?.close()

    this.mic = null
    this.audio = null
    this.capture = null

    return wav(this.chunks, this.rate)
  }

  /** Бросает запись, ничего не возвращая. */
  cancel(): void {
    this.mic?.getTracks().forEach((track) => track.stop())
    void this.audio?.close()
    this.chunks = []
    this.mic = null
    this.audio = null
    this.capture = null
  }
}

/**
 * Собирает WAV из накопленных блоков.
 *
 * Заголовок пишется здесь, а не на сервере: без него это просто поток
 * байтов, и распознаванию пришлось бы угадывать частоту и разрядность.
 */
function wav(chunks: Float32Array[], rate: number): Blob {
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0)
  const buffer = new ArrayBuffer(44 + total * 2)
  const view = new DataView(buffer)

  const text = (offset: number, value: string) => {
    for (let i = 0; i < value.length; i++) view.setUint8(offset + i, value.charCodeAt(i))
  }

  text(0, 'RIFF')
  view.setUint32(4, 36 + total * 2, true)
  text(8, 'WAVE')
  text(12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true) // PCM без сжатия
  view.setUint16(22, 1, true) // моно
  view.setUint32(24, rate, true)
  view.setUint32(28, rate * 2, true)
  view.setUint16(32, 2, true)
  view.setUint16(34, 16, true)
  text(36, 'data')
  view.setUint32(40, total * 2, true)

  let offset = 44
  for (const chunk of chunks) {
    for (let i = 0; i < chunk.length; i++) {
      // Ограничение перед переводом: значения за пределами диапазона
      // при переполнении превращаются в треск.
      const v = Math.max(-1, Math.min(1, chunk[i] ?? 0))
      view.setInt16(offset, v < 0 ? v * 32768 : v * 32767, true)
      offset += 2
    }
  }

  return new Blob([buffer], { type: 'audio/wav' })
}

/**
 * Отправляет запись на распознавание.
 *
 * Возвращает текст, а не вставляет его сам: распознавание ошибается на
 * именах и числах, и последнее слово должно остаться за человеком.
 */
export async function transcribe(blob: Blob): Promise<string> {
  const response = await fetch('/api/setup/transcribe', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'audio/wav' },
    body: blob,
  })

  if (!response.ok) throw new Error('не удалось распознать')

  const data = (await response.json()) as { text: string }
  return data.text
}
